import { eventChannel } from "redux-saga";
import { call, put, take, select, race, delay } from "redux-saga/effects";
import { selectUserCredentials } from "../../../state/userCredential/userCredentialSlice";
import { logoutRequestAction } from "../../logout/action";
import { refreshAccessTokenSaga } from "../../auth/refreshAccessToken/refreshAccessToken";
import { fetchCrmStatusAction } from "../../integration/fetchCrmStatus/action";
import { fetchSubscriptionUsageSaga } from "../../analytic/analyticSubscription/fetchSubscriptionUsage/fetchSubscriptionUsage";
import { addNotification, selectNotificationsEnabled } from "../../../state/notification/notificationSlice";
import bugsnagClient from "../../../services/bugsnag/bugsnag";
import { showSnackbar } from "../../../state/snackbar/snackbarSlice";
import { sendLogsAction } from "../../logs/sendLogs/action";
import { env } from "../../../config/env";
import { getAppVersion } from "../../../browser/deviceIdentity";

// Server pings every 25s; 3 missed pings = dead connection.
const PING_TIMEOUT_MS = 75 * 1000;
const RECONNECT_DELAYS_MS = [2000, 5000, 15000, 30000];
const STABLE_SESSION_MS = 60 * 1000;

function createSseChannel(uid, jwt, appVersion, domain) {
    return eventChannel((emitter) => {
        const baseUrl = env.BACKEND_SERVER;
        const url = `${baseUrl}/stream/${uid}?token=${jwt}&appVersion=${appVersion}&domain=${domain}`;

        console.log("Opening SSE connection for", uid);
        const evtSource = new EventSource(url);

        evtSource.addEventListener("ping", () => {
            emitter({ type: "PING" });
        });

        evtSource.addEventListener("forceLogout", () => {
            console.warn("Received force logout");
            emitter({ type: "FORCE_LOGOUT" });
        });

        evtSource.addEventListener("notification", (event) => {
            try {
                const data = JSON.parse(event.data || "{}");
                emitter({ type: "NOTIFICATION", data });
            } catch (e) {
                console.error("Failed to parse notification SSE data", e);
            }
        });

        evtSource.addEventListener("configRefresh", (event) => {
            console.warn("Received config refresh");
            emitter({ type: "CONFIG_REFRESH" });
        });

        evtSource.addEventListener("integrationUpdate", (event) => {
            try {
                const data = JSON.parse(event.data || "{}");
                emitter({ type: "INTEGRATION_UPDATE", data });
            } catch (e) {
                console.error("Failed to parse integrationUpdate SSE data", e);
            }
        });

        evtSource.addEventListener("sendLogs", (event) => {
            try {
                const data = JSON.parse(event.data || "{}");
                emitter({ type: "SEND_LOGS", data });
            } catch (e) {
                console.error("Failed to parse sendLogs SSE data", e);
            }
        });

        evtSource.onerror = (err) => {
            console.error("SSE error:", err);
            emitter({ type: "ERROR", error: err });
        };

        // Close callback (for saga cleanup)
        return () => {
            console.log("Closing SSE connection");
            evtSource.close();
        };
    });
}

// One connection lifetime; returns why it ended: 'error' | 'silent' | 'no-credentials'.
function* runSseSession() {
    const { uid, displayName, email, domain, jwt } = yield select(selectUserCredentials);
    if (!uid || !jwt) return "no-credentials";

    const appVersion = yield call(getAppVersion);
    const channel = yield call(createSseChannel, uid, jwt, appVersion, domain);

    try {
        while (true) {
            const { event, timeout } = yield race({
                event: take(channel),
                timeout: delay(PING_TIMEOUT_MS),
            });

            if (timeout) {
                console.warn("SSE silent for too long — assuming dead connection");
                return "silent";
            }

            switch (event.type) {
                case "PING":
                    break;

                case "FORCE_LOGOUT":
                    console.debug("Application opened elsewhere. Logging out...");
                    bugsnagClient.notify(new Error('Force logout triggered'), (bugsnagEvent) => {
                        bugsnagEvent.context = 'connectSseSaga';
                        bugsnagEvent.addMetadata('metadata', {
                            source: 'connectSseSaga',
                            reason: 'Application opened on another device',
                            uid,
                            displayName,
                            email,
                            appVersion
                        });
                    });
                    yield put(logoutRequestAction());
                    break;

                case "NOTIFICATION":
                    console.debug("Received notification:", event.data);

                    yield put(addNotification(event.data));

                    const enabled = yield select(selectNotificationsEnabled);

                    if (enabled) {

                        let severity = "info";

                        switch (event.data.type) {
                            case "call.analyzed":
                                severity = "success";
                                break;

                            case "admin.broadcast":
                                severity = "info";
                                break;

                            case "system.alert":
                                severity = "warning";
                                break;

                            case "error":
                                severity = "error";
                                break;

                            default:
                                severity = "info";
                        }

                        yield put(
                            showSnackbar({
                                message: event.data.message || "New Notification",
                                severity,
                            })
                        );
                    }
                    break;

                case "CONFIG_REFRESH":
                    console.debug("Config changed. Refreshing access token...");

                    yield call(refreshAccessTokenSaga);
                    yield call(fetchSubscriptionUsageSaga);

                    yield put(
                        showSnackbar({
                            message: "Your account settings have been updated",
                            severity: "info",
                        })
                    );
                    break;

                case "INTEGRATION_UPDATE":
                    console.debug("Integration updated:", event.data);
                    yield put(fetchCrmStatusAction({ crm: event.data.crm }));
                    break;

                case "SEND_LOGS":
                    yield put(sendLogsAction());
                    break;

                case "ERROR":
                    console.error("SSE connection error:", event.error);
                    return "error";

                default:
                    console.debug("Unhandled SSE event:", event);
            }
        }
    } finally {
        channel.close();
    }
}

// Reconnect loop: fresh connection (current jwt) after any failure, with backoff;
// cancellation from logout/suspend/disconnect ends the loop.
export function* connectSseSaga() {
    let attempt = 0;

    while (true) {
        const startedAt = Date.now();
        const reason = yield call(runSseSession);

        if (reason === "no-credentials") return;

        if (Date.now() - startedAt >= STABLE_SESSION_MS) attempt = 0;
        const delayMs = RECONNECT_DELAYS_MS[Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)];
        attempt++;

        console.warn(`SSE connection lost (${reason}); reconnecting in ${delayMs / 1000}s`);
        yield delay(delayMs);
    }
}